/**
 * 腾讯云 COS 直传工具函数
 * 用于替换原有的后端代理上传方式
 */
import { getSTSToken } from '@/api/tool/common'
import { uploadOss, ensureHttpsUrl } from '@/api/tool/oss'
import COS from 'cos-js-sdk-v5'

/**
 * 上传文件到腾讯云 COS
 * @param {Object} file - 文件对象
 * @param {String} prefix - 路径前缀，如 'video', 'image', 'subtitle/zh' 等
 * @param {Function} onProgress - 进度回调函数，参数为 { percent: 0-100 }
 * @returns {Promise<String>} 返回文件 URL
 */
export async function uploadToCos(file, prefix, onProgress) {
  try {
    // 1. 生成文件路径
    const filePath = uploadOss(file.name, prefix)

    // 2. 使用 getAuthorization 回调方式，动态获取临时凭证
    const cos = new COS({
      getAuthorization: async function(options, callback) {
        try {
          const stsToken = await getSTSToken()
          if (!stsToken || !stsToken.data) {
            callback('获取COS凭证失败')
            return
          }

          const startTime = Math.floor(Date.now() / 1000)
          const expiredTime = startTime + 1800

          callback({
            TmpSecretId: stsToken.data.accessKeyId,
            TmpSecretKey: stsToken.data.accessKeySecret,
            XCosSecurityToken: stsToken.data.securityToken,
            ExpiredTime: expiredTime,
            StartTime: startTime
          })
        } catch (error) {
          callback(error.message || '获取COS凭证失败')
        }
      },
      Protocol: 'https:',
      Timeout: 60000,
      UseAccelerate: false,
      CorrectClockSkew: true
    })

    // 3. 获取 bucket 和 region 信息
    const stsToken = await getSTSToken()
    if (!stsToken || !stsToken.data) {
      throw new Error('获取COS凭证失败')
    }

    const bucket = stsToken.data.bucket
    const region = stsToken.data.region

    // 4. 判断文件大小，选择上传方式
    const fileSize = file.size || file.originFileObj?.size || 0
    const isLargeFile = fileSize > 5 * 1024 * 1024 // 5MB

    // 获取文件对象（可能是 file 或 file.originFileObj）
    const fileObj = file.originFileObj || file

    let uploadResult
    if (isLargeFile) {
      uploadResult = await cos.uploadFile({
        Bucket: bucket,
        Region: region,
        Key: filePath,
        Body: fileObj,
        SliceSize: 1024 * 1024 * 5,
        ContentType: fileObj.type || 'application/octet-stream',
        Headers: {},
        onProgress: function(progressData) {
          if (onProgress) {
            const percent = Math.round(progressData.percent * 100)
            onProgress({ percent })
          }
        }
      })
    } else {
      uploadResult = await cos.putObject({
        Bucket: bucket,
        Region: region,
        Key: filePath,
        Body: fileObj,
        ContentType: fileObj.type || 'application/octet-stream',
        Headers: {},
        onProgress: function(progressData) {
          if (onProgress) {
            let percent = 0
            if (progressData.percent !== undefined) {
              percent = Math.round(progressData.percent * 100)
            } else if (progressData.loaded && progressData.total) {
              percent = Math.round((progressData.loaded / progressData.total) * 100)
            }
            onProgress({ percent })
          }
        }
      })
    }

    // 5. 检查上传结果并构建 URL
    if (uploadResult && (uploadResult.statusCode === 200 || uploadResult.ETag || uploadResult.Location)) {
      let url = ''
      if (stsToken.data.staticUrl) {
        url = stsToken.data.staticUrl + (stsToken.data.staticUrl.endsWith('/') ? '' : '/') + filePath
      } else if (uploadResult.Location) {
        url = uploadResult.Location
      } else {
        url = `https://${bucket}.cos.${region}.myqcloud.com/${filePath}`
      }
      // 确保 URL 包含 https:// 前缀
      return ensureHttpsUrl(url)
    } else {
      throw new Error('上传失败，返回结果异常')
    }
  } catch (error) {
    console.error('上传失败:', error)
    throw error
  }
}

/**
 * 创建上传处理函数（用于 a-upload 的 customRequest）
 * @param {String} prefix - 路径前缀
 * @param {Function} onSuccess - 成功回调，参数为 (url)
 * @param {Function} onError - 失败回调，参数为 (error)
 * @param {Function} onProgress - 进度回调，参数为 (percent)
 * @returns {Function} 上传处理函数
 */
export function createUploadHandler(prefix, onSuccess, onError, onProgress) {
  return async (options) => {
    const { file, onSuccess: defaultOnSuccess, onError: defaultOnError, onProgress: defaultOnProgress } = options

    try {
      const url = await uploadToCos(
        file,
        prefix,
        (progress) => {
          if (onProgress) {
            onProgress(progress.percent)
          }
          if (defaultOnProgress) {
            defaultOnProgress(progress)
          }
        }
      )

      if (onSuccess) {
        onSuccess(url)
      }
      if (defaultOnSuccess) {
        defaultOnSuccess({ url }, file)
      }
    } catch (error) {
      const errorMsg = error.message || '上传失败'
      if (onError) {
        onError(error)
      }
      if (defaultOnError) {
        defaultOnError(error)
      }
    }
  }
}

